import { DeliveryMethod, StoreOrderState } from '@/buyers/_gen/gql'
import { PaymentMethod } from '@/suppliers/_gen/gql'
import A from '@/gf/components/A'
import Action from '@/gf/components/Action'
import ButtonLink from '@/gf/components/ButtonLinkOld'
import Button from '@/gf/components/ButtonOld'
import LineItemC from '@/gf/components/LineItem'
import LineItemsCard from '@/gf/components/LineItemsCard'
import Link from '@/gf/components/Link'
import ModalForm from '@/gf/components/ModalForm'
import Spinner from '@/gf/components/Spinner'
import useConfig from '@/gf/hooks/useConfig'
import useMsgs from '@/gf/hooks/useMsgs'
import useToggle from '@/gf/hooks/useToggle'
import Money from '@/gf/modules/Money'
import StoreOrderM from '@/gf/modules/StoreOrder'
import Time from '@/gf/modules/Time'
import UserContext from '@/gf/modules/User'
import { copyText } from '@/gf/modules/utils'
import { Action as ActionT, ElementAction, Shipment, ShipmentLabel, User } from '@/types'
import { LinkIcon, TruckIcon } from '@heroicons/react/24/outline'
import { useNavigate } from 'react-router-dom'
import useSession from '../../hooks/useSession'
import useUncancelStoreOrder from '../../hooks/useUncancelStoreOrder'
import useUndoStoreOrder from '../../hooks/useUndoStoreOrder'
import { StoreOrder } from './useFetchStoreOrder'

type LineItem = StoreOrder['lineItems'][number]

const UndoModal = ({ open, onClose, storeOrderId }) => {
  const [_msgs, Msgr] = useMsgs()
  const [spinnerAdd, spinner] = useToggle()
  const undoStoreOrder = useUndoStoreOrder()

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    spinner.on()
    Msgr.clear()

    undoStoreOrder({ id: storeOrderId })
      .then((_result) => window.location.reload())
      .catch((error) => {
        spinner.off()
        Msgr.add(
          error?.message || 'Whoops, something went wrong. Please contact engineering.',
          'negative'
        )
      })
  }

  return (
    <ModalForm open={open} onClose={onClose} title="Undo" onSubmit={onSubmit}>
      {spinnerAdd ? (
        <Spinner />
      ) : (
        <>
          <div className="font-bold">note: this only works for direct pay orders</div>
          <p className="pt-4">
            {' '}
            <span>
              Click submit to put this store order back into open quote and reopen the request. This
              is useful to correct prices after the buyer has approved and the supplier has marked
              ready, or shipped.
            </span>{' '}
          </p>
        </>
      )}
    </ModalForm>
  )
}

// Returns each shipment and its line items with the updated quantity
// *** This includes shipments that have not be shipped yet ***
const getShipments = (storeOrder: Pick<StoreOrder, 'lineItems'>) =>
  Object.values(
    storeOrder.lineItems.reduce<Record<string, { shipment: Shipment; lineItems: LineItem[] }>>(
      (storeOrderShipments, lineItem) => ({
        ...storeOrderShipments,
        ...lineItem.shipmentItems.reduce<
          Record<string, { shipment: Shipment; lineItems: LineItem[] }>
        >(
          (lineItemShipments, { shipment, quantity }) => ({
            ...lineItemShipments,
            [shipment.id]: lineItemShipments[shipment.id]
              ? {
                  ...lineItemShipments[shipment.id],
                  lineItems: [
                    ...lineItemShipments[shipment.id].lineItems,
                    { ...lineItem, quantity },
                  ],
                }
              : {
                  lineItems: [{ ...lineItem, quantity }],
                  shipment,
                },
          }),
          storeOrderShipments
        ),
      }),
      {}
    )
  )

const LineItems = ({ user, lineItems }: { user: Pick<User, 'role'>; lineItems: LineItem[] }) => (
  <>
    {lineItems.map((li) => (
      <LineItemC item={li} linkToMarketplace key={li.id} isAdmin={UserContext.isAdmin(user)} />
    ))}
  </>
)

const TrackDeliveryLink = ({ url, text = 'Track Delivery' }) => (
  <A.S href={url} target="_blank" rel="noreferrer">
    <TruckIcon className="text-gearflow h-5 -mt-1 inline-block" /> {text}
  </A.S>
)

const UncancelModal = ({ open, onClose, storeOrderId }) => {
  const [_msgs, Msgr] = useMsgs()
  const [spinnerAdd, spinner] = useToggle()
  const uncancelStoreOrder = useUncancelStoreOrder()

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    spinner.on()
    Msgr.clear()

    uncancelStoreOrder({ id: storeOrderId })
      .then((_result) => window.location.reload())
      .catch((_error) => {
        spinner.off()
        Msgr.add('Whoops, something went wrong. Please contact engineering.', 'negative')
      })
  }

  return (
    <ModalForm open={open} onClose={onClose} title="Uncancel" onSubmit={onSubmit}>
      {spinnerAdd ? (
        <Spinner />
      ) : (
        <>
          <p className="pt-4">
            {' '}
            <span>Click to uncancel store order</span>{' '}
          </p>
        </>
      )}
    </ModalForm>
  )
}

// Define cards for different groups of line items
const ReceivedCard = ({
  user,
  storeOrder,
  onApproveStoreOrder,
  onCancelStoreOrder,
}: {
  user: Pick<User, 'role'>
  storeOrder: Pick<
    StoreOrder,
    'id' | 'state' | 'lineItems' | 'timingDetails' | 'curriDeliveries' | 'paymentMethod'
  >
  onApproveStoreOrder: () => void
  onCancelStoreOrder: () => void
}) => {
  const secondaryActions: (ActionT | ElementAction)[] = [
    {
      id: 2,
      element: <Action.S onClick={onCancelStoreOrder}>Cancel Order</Action.S>,
    },
  ]

  if (storeOrder.paymentMethod !== PaymentMethod.Stripe) {
    secondaryActions.push({
      id: 3,
      element: (
        <A.S href={`/orders/${storeOrder.id}/edit`} rel="noreferrer">
          Edit Order
        </A.S>
      ),
    })
  }

  const trackActions: (ActionT | ElementAction)[] =
    storeOrder?.curriDeliveries?.length > 0
      ? storeOrder.curriDeliveries.map((curriDelivery, index) => ({
          id: index + 4,
          element: <TrackDeliveryLink url={curriDelivery.trackingUrl} />,
        }))
      : []

  const secondaryFooterActions = trackActions.concat(secondaryActions)

  return (
    <LineItemsCard
      title="Pending Items"
      subsectionText={['These items are awaiting your approval.']}
      primaryFooterAction={{
        id: 1,
        title: 'Approve Order',
        callBack: onApproveStoreOrder,
      }}
      secondaryFooterActions={secondaryFooterActions}
      lineItems={<LineItems user={user} lineItems={storeOrder.lineItems} />}
      timingDetails={storeOrder.timingDetails}
    />
  )
}

const OpenQuoteCard = ({
  user,
  storeOrder,
  onCancelStoreOrder,
}: {
  user: Pick<User, 'role'>
  storeOrder: Pick<StoreOrder, 'id' | 'lineItems' | 'timingDetails' | 'token'>
  onCancelStoreOrder: () => void
}) => {
  const [_msgs, msgsMgr] = useMsgs()
  const { loginBaseUrl, buyersUrl } = useConfig()
  const navigate = useNavigate()
  const linkTitle = 'Copy Link'
  const linkUrl = StoreOrderM.copyLinkUrl(storeOrder, loginBaseUrl, buyersUrl)

  const copyReviewLink = () => {
    copyText(linkUrl)
    msgsMgr.add('Link copied to clipboard 👍', 'positive')
  }

  const allSecondaryActions = [
    {
      id: 2,
      title: 'Cancel Order',
      callBack: onCancelStoreOrder,
    },
    {
      id: 3,
      title: 'Edit Order',
      callBack: () => navigate(`/orders/${storeOrder.id}/edit`),
    },
    {
      id: 4,
      element: <Button title="Approve Order" disabled />,
    },
  ]

  const secondaryActions =
    linkUrl === ''
      ? allSecondaryActions
      : [
          {
            id: 1,
            element: (
              <Button
                className="flex flex-row gap-x-2 flex-nowrap items-center"
                onClick={copyReviewLink}
                primary={false}
              >
                <LinkIcon className="-mx-1 h-5 w-5 text-gray-400" />
                {linkTitle}
              </Button>
            ),
          },
          ...allSecondaryActions,
        ]

  return (
    <LineItemsCard
      title="Awaiting Approval"
      lineItems={<LineItems user={user} lineItems={storeOrder.lineItems} />}
      subsectionText={["These items are awaiting the Customer's approval."]}
      secondaryFooterActions={secondaryActions}
      timingDetails={storeOrder.timingDetails}
    />
  )
}

const QuoteDeniedCard = ({
  user,
  storeOrder,
  quoteDeniedReason,
  onCancelStoreOrder,
}: {
  user: Pick<User, 'role'>
  storeOrder: Pick<StoreOrder, 'id' | 'lineItems' | 'timingDetails'>
  quoteDeniedReason?: string
  onCancelStoreOrder: () => void
}) => {
  const navigate = useNavigate()
  return (
    <LineItemsCard
      title="Awaiting Your Response"
      subsectionText={
        quoteDeniedReason
          ? ['Customer feedback:', quoteDeniedReason]
          : ['The customer denied your quote.']
      }
      lineItems={<LineItems user={user} lineItems={storeOrder.lineItems} />}
      secondaryFooterActions={[
        {
          id: 1,
          title: 'Cancel Order',
          callBack: onCancelStoreOrder,
        },
        {
          id: 2,
          title: 'Edit Order',
          callBack: () => navigate(`/orders/${storeOrder.id}/edit`),
        },
        {
          id: 3,
          element: <Button title="Approve Order" disabled />,
        },
      ]}
      timingDetails={storeOrder.timingDetails}
    />
  )
}

const ReadyForPickupCard = ({
  user,
  storeOrder,
  undoToggler,
}: {
  user: Pick<User, 'role'>
  storeOrder: Pick<
    StoreOrder,
    'id' | 'lineItems' | 'timingDetails' | 'curriDeliveries' | 'paymentMethod'
  >
  undoToggler: { on: () => void }
}) => {
  const secondaryFooterActions: (ActionT | ElementAction)[] = []

  // Undo deletes shipments and resets the order to an open quote. It only runs for
  // direct-pay orders (see Gf.Suppliers.undo_store_order/1) — for other payment methods
  // the charge and supplier payout are already settled, so we don't offer the button.
  if (UserContext.isAdmin(user) && storeOrder.paymentMethod === PaymentMethod.Direct) {
    secondaryFooterActions.push({
      id: 1,
      element: (
        <Button onClick={undoToggler.on} primary={false}>
          Undo
        </Button>
      ),
    })
  }

  if (storeOrder?.curriDeliveries?.length > 0) {
    storeOrder.curriDeliveries.forEach((curriDelivery) => {
      secondaryFooterActions.push({
        id: 2,
        element: <TrackDeliveryLink url={curriDelivery.trackingUrl} />,
      })
    })
  }

  if (storeOrder.paymentMethod === PaymentMethod.Direct) {
    secondaryFooterActions.push({
      id: 3,
      element: (
        <A.S href={`/orders/${storeOrder.id}/edit`} rel="noreferrer">
          Edit Order
        </A.S>
      ),
    })
  }

  return (
    <LineItemsCard
      title="Ready"
      lineItems={<LineItems user={user} lineItems={storeOrder.lineItems} />}
      timingDetails={storeOrder.timingDetails}
      secondaryFooterActions={secondaryFooterActions}
    />
  )
}

const AwaitingReadyForPickupCard = ({
  user,
  storeOrder,
  state,
  onReadyForPickup,
}: {
  user: Pick<User, 'role'>
  storeOrder: Pick<
    StoreOrder,
    'id' | 'lineItems' | 'timingDetails' | 'curriDeliveries' | 'paymentMethod'
  >
  state: StoreOrderState
  onReadyForPickup: () => void
}) => {
  const secondaryFooterActions: (ActionT | ElementAction)[] =
    storeOrder?.curriDeliveries?.length > 0
      ? storeOrder.curriDeliveries.map((curriDelivery) => ({
          id: 1,
          element: <TrackDeliveryLink url={curriDelivery.trackingUrl} />,
        }))
      : []

  if (storeOrder.paymentMethod === PaymentMethod.Direct) {
    secondaryFooterActions.push({
      id: 2,
      element: (
        <A.S href={`/orders/${storeOrder.id}/edit`} rel="noreferrer">
          Edit Order
        </A.S>
      ),
    })
  }

  return (
    <LineItemsCard
      title={state !== 'REFUNDED' ? 'Preparing Items' : 'Items'}
      primaryFooterAction={
        state === 'REFUNDED'
          ? // Disable actions when an order is refunded
            undefined
          : {
              id: 1,
              title: 'Ready',
              callBack: () => onReadyForPickup(),
            }
      }
      lineItems={<LineItems user={user} lineItems={storeOrder.lineItems} />}
      subsectionText={
        state !== 'REFUNDED'
          ? ['Once you have prepared these items, click the "Ready for Pickup" button.']
          : undefined
      }
      timingDetails={storeOrder.timingDetails}
      secondaryFooterActions={secondaryFooterActions}
    />
  )
}

const BuyerReceivedPickupCard = ({
  user,
  storeOrder,
  undoToggler,
}: {
  user: Pick<User, 'role'>
  storeOrder: Pick<
    StoreOrder,
    'id' | 'lineItems' | 'timingDetails' | 'curriDeliveries' | 'paymentMethod'
  >
  undoToggler: { on: () => void }
}) => {
  const secondaryFooterActions: (ActionT | ElementAction)[] = []

  // Undo deletes shipments and resets the order to an open quote. It only runs for
  // direct-pay orders (see Gf.Suppliers.undo_store_order/1) — for other payment methods
  // the charge and supplier payout are already settled, so we don't offer the button.
  if (UserContext.isAdmin(user) && storeOrder.paymentMethod === PaymentMethod.Direct) {
    secondaryFooterActions.push({
      id: 1,
      element: (
        <Button onClick={undoToggler.on} primary={false}>
          Undo
        </Button>
      ),
    })
  }

  if (storeOrder?.curriDeliveries?.length > 0) {
    storeOrder.curriDeliveries.forEach((curriDelivery) => {
      secondaryFooterActions.push({
        id: 2,
        element: <TrackDeliveryLink url={curriDelivery.trackingUrl} />,
      })
    })
  }

  if (storeOrder.paymentMethod === PaymentMethod.Direct) {
    secondaryFooterActions.push({
      id: 3,
      element: (
        <A.S href={`/orders/${storeOrder.id}/edit`} rel="noreferrer">
          Edit Order
        </A.S>
      ),
    })
  }

  return (
    <LineItemsCard
      title="Items"
      lineItems={<LineItems user={user} lineItems={storeOrder.lineItems} />}
      timingDetails={storeOrder.timingDetails}
      secondaryFooterActions={secondaryFooterActions}
    />
  )
}

const PrintShippingLabelElementButton = ({ label }: { label: ShipmentLabel }) => (
  <ButtonLink primary={false} href={label.url} target="_blank" rel="noreferrer">
    Print Shipping Label
  </ButtonLink>
)

const ShippedCard = ({
  user,
  lineItems,
  storeOrder,
  shipment,
  setShipmentToEdit,
  undoToggler,
}: {
  user: Pick<User, 'role'>
  lineItems: LineItem[]
  storeOrder: Pick<StoreOrder, 'id' | 'timingDetails' | 'paymentMethod'>
  shipment: Shipment
  setShipmentToEdit: (shipment: Shipment) => void
  undoToggler: { on: () => void }
}) => {
  // Subsection text
  const subsectionText: string[] = []
  if (shipment.carrier && !shipment.link) subsectionText.push(`Carrier: ${shipment.carrier}`)
  if (shipment.tracking && !shipment.link)
    subsectionText.push(`Tracking number: ${shipment.tracking}`)
  if (UserContext.isAdmin(user) && shipment.label)
    subsectionText.push(`Label cost: ${Money.format(shipment.label.cost)}`)
  if (shipment.receivedAt)
    subsectionText.push(`Customer received on ${Time.formatDateNoTime(shipment.receivedAt)}`)
  // Secondary footer actions
  const secondaryFooterActions: (ActionT | ElementAction)[] = []

  // Undo deletes shipments and resets the order to an open quote. It only runs for
  // direct-pay orders (see Gf.Suppliers.undo_store_order/1) — for other payment methods
  // the charge and supplier payout are already settled, so we don't offer the button.
  if (UserContext.isAdmin(user) && storeOrder.paymentMethod === PaymentMethod.Direct) {
    secondaryFooterActions.push({
      id: 1,
      element: (
        <Button onClick={undoToggler.on} primary={false}>
          Undo
        </Button>
      ),
    })
  }

  if (shipment.label)
    secondaryFooterActions.push({
      id: 2,
      element: <PrintShippingLabelElementButton label={shipment.label} />,
    })
  secondaryFooterActions.push({
    id: 3,
    element: (
      <Button onClick={() => setShipmentToEdit(shipment)} primary={false}>
        Edit Shipping
      </Button>
    ),
  })

  if (storeOrder.paymentMethod === PaymentMethod.Direct) {
    secondaryFooterActions.push({
      id: 4,
      element: (
        <A.S href={`/orders/${storeOrder.id}/edit`} rel="noreferrer">
          Edit Order
        </A.S>
      ),
    })
  }

  return (
    /* classnames on div are a hack so that I don't have rewrite all of the layout */
    /* otherwise, there is no margin between the <LineItemsCard />s */
    <div className="mt-6 first:mt-0">
      <LineItemsCard
        title={shipment.receivedAt ? 'Delivered' : 'Shipped'}
        primaryFooterAction={
          shipment.link
            ? {
                id: 1,
                element: <TrackDeliveryLink url={shipment.link} text="Track Shipment" />,
              }
            : undefined
        }
        secondaryFooterActions={secondaryFooterActions}
        subsectionText={subsectionText}
        lineItems={<LineItems user={user} lineItems={lineItems} />}
        timingDetails={storeOrder.timingDetails}
      />
    </div>
  )
}

const ReadyToShipCard = ({
  user,
  lineItems,
  storeOrder,
  shipment,
  state,
  onMarkAsShipped,
}: {
  user: Pick<User, 'role'>
  lineItems: LineItem[]
  storeOrder: Pick<StoreOrder, 'id' | 'timingDetails' | 'curriDeliveries' | 'paymentMethod'>
  shipment: Shipment
  state: StoreOrderState
  onMarkAsShipped: (shipment: Shipment) => void
}) => {
  const subsectionText: string[] = []
  if (shipment.carrier) subsectionText.push(`Carrier: ${shipment.carrier}`)
  if (shipment.label)
    subsectionText.push(`Ship date: ${Time.formatDateWithWeekday(shipment.label.shipDate)}`)
  if (UserContext.isAdmin(user) && shipment.label)
    subsectionText.push(`Label cost: ${Money.format(shipment.label.cost)}`)

  const secondaryFooterActions: (ActionT | ElementAction)[] = shipment.label
    ? [
        {
          id: 2,
          element: <PrintShippingLabelElementButton label={shipment.label} />,
        },
      ]
    : []

  if (storeOrder.paymentMethod === PaymentMethod.Direct) {
    secondaryFooterActions.push({
      id: 3,
      element: (
        <A.S href={`/orders/${storeOrder.id}/edit`} rel="noreferrer">
          Edit Order
        </A.S>
      ),
    })
  }

  return (
    <LineItemsCard
      title="Ready to Ship"
      primaryFooterAction={
        state === 'REFUNDED'
          ? // Disable actions when an order is refunded
            undefined
          : {
              id: 1,
              title: 'Mark as Shipped',
              callBack: () => onMarkAsShipped(shipment),
            }
      }
      secondaryFooterActions={
        secondaryFooterActions.length > 0 ? secondaryFooterActions : undefined
      }
      subsectionText={subsectionText}
      lineItems={<LineItems user={user} lineItems={lineItems} />}
      timingDetails={storeOrder.timingDetails}
    />
  )
}

const OrderItemCards = ({
  storeOrder,
  onCancelStoreOrder,
  onApproveStoreOrder,
  onCreateShipment,
  onMarkAsShipped,
  onReadyForPickup,
  setShipmentToEdit,
}: {
  storeOrder: StoreOrder
  onCancelStoreOrder: () => void
  onApproveStoreOrder: () => void
  onCreateShipment: (gearflowShipping: boolean) => void
  onMarkAsShipped: (shipment: Shipment) => void
  onReadyForPickup: () => void
  setShipmentToEdit: (shipment: Shipment) => void
}) => {
  const { user } = useSession()

  const shipments = getShipments(storeOrder)
  const unshipped = StoreOrderM.getUnshipped(storeOrder.lineItems)
  const [showUncancel, uncancelToggler] = useToggle()
  const [showUndo, undoToggler] = useToggle()

  const uncancelActions = (() => {
    const result: (ActionT | ElementAction)[] = []

    if (UserContext.isAdmin(user) && storeOrder?.state === 'CANCELLED') {
      result.push({
        id: 1,
        element: (
          <Button onClick={uncancelToggler.on} primary={false}>
            Uncancel
          </Button>
        ),
      })
    }

    if (storeOrder.paymentMethod === PaymentMethod.Direct) {
      result.push({
        id: 2,
        element: (
          <A.S href={`/orders/${storeOrder.id}/edit`} rel="noreferrer">
            Edit Order
          </A.S>
        ),
      })
    }

    return result
  })()

  const awaitingShipmentActions = (() => {
    const result: ElementAction[] = []

    if (
      UserContext.isAdmin(user) &&
      ['PROCESSING', 'READY_FOR_PICKUP', 'SHIPPED', 'BUYER_RECEIVED'].includes(storeOrder?.state)
    )
      result.push({
        id: 1,
        element: (
          <Button onClick={undoToggler.on} primary={false}>
            Undo
          </Button>
        ),
      })

    if (
      storeOrder.state === StoreOrderState.PartiallyShipped &&
      !storeOrder.completedAt &&
      storeOrder.refunds.length > 0
    )
      result.push({
        id: 2,
        element: <Link.S to={`/orders/${storeOrder.id}/complete`}>Order Completed</Link.S>,
      })

    if (storeOrder.paymentMethod === PaymentMethod.Direct) {
      result.push({
        id: 3,
        element: (
          <A.S href={`/orders/${storeOrder.id}/edit`} rel="noreferrer">
            Edit Order
          </A.S>
        ),
      })
    }

    return result
  })()

  return (
    <>
      <UndoModal open={showUndo} onClose={undoToggler.off} storeOrderId={storeOrder.id} />
      <UncancelModal
        open={showUncancel}
        onClose={uncancelToggler.off}
        storeOrderId={storeOrder.id}
      />
      <div>
        {/* Order Item Cards */}
        {storeOrder.state === 'RECEIVED' ? (
          <ReceivedCard
            user={user}
            storeOrder={storeOrder}
            onApproveStoreOrder={onApproveStoreOrder}
            onCancelStoreOrder={onCancelStoreOrder}
          />
        ) : storeOrder.state === 'OPEN_QUOTE' ? (
          <OpenQuoteCard
            user={user}
            storeOrder={storeOrder}
            onCancelStoreOrder={onCancelStoreOrder}
          />
        ) : storeOrder.state === 'QUOTE_DENIED' ? (
          <QuoteDeniedCard
            user={user}
            storeOrder={storeOrder}
            quoteDeniedReason={StoreOrderM.quoteDeniedReason(storeOrder)}
            onCancelStoreOrder={onCancelStoreOrder}
          />
        ) : storeOrder.state === 'CANCELLED' ? (
          <LineItemsCard
            title="Cancelled Items"
            secondaryFooterActions={uncancelActions}
            lineItems={<LineItems user={user} lineItems={storeOrder.lineItems} />}
          />
        ) : storeOrder.state === 'READY_FOR_PICKUP' ? (
          storeOrder.deliveryMethod === DeliveryMethod.VendorDelivery ? (
            <LineItemsCard
              title="Out For Delivery"
              lineItems={<LineItems user={user} lineItems={storeOrder.lineItems} />}
              timingDetails={storeOrder.timingDetails}
              secondaryFooterActions={
                storeOrder.paymentMethod === PaymentMethod.Direct
                  ? [
                      {
                        id: 1,
                        element: (
                          <A.S href={`/orders/${storeOrder.id}/edit`} rel="noreferrer">
                            Edit Order
                          </A.S>
                        ),
                      },
                    ]
                  : []
              }
            />
          ) : (
            <ReadyForPickupCard user={user} storeOrder={storeOrder} undoToggler={undoToggler} />
          )
        ) : storeOrder.state === 'BUYER_RECEIVED' && storeOrder.pickup ? (
          <BuyerReceivedPickupCard user={user} storeOrder={storeOrder} undoToggler={undoToggler} />
        ) : StoreOrderM.isApprovedBySupplier(storeOrder) && storeOrder.pickup ? (
          <AwaitingReadyForPickupCard
            user={user}
            storeOrder={storeOrder}
            state={storeOrder.state}
            onReadyForPickup={onReadyForPickup}
          />
        ) : StoreOrderM.isApprovedBySupplier(storeOrder) ? (
          // (after the customer is charged)
          <>
            {unshipped?.length > 0 && (
              <LineItemsCard
                title="Awaiting Shipment"
                primaryFooterAction={
                  storeOrder.state !== 'REFUNDED' && !storeOrder.completedAt
                    ? {
                        id: 1,
                        title: 'Create Shipment',
                        callBack: () => onCreateShipment(storeOrder.gearflowShipping),
                      }
                    : undefined
                }
                secondaryFooterActions={awaitingShipmentActions}
                lineItems={<LineItems user={user} lineItems={unshipped} />}
                timingDetails={storeOrder.timingDetails}
              />
            )}

            {shipments
              // Show shipped items at the bottom
              .sort((a, b) =>
                !a.shipment.shippedAt
                  ? -1
                  : !b.shipment.shippedAt
                    ? 1
                    : a.shipment.shippedAt < b.shipment.shippedAt
                      ? 1
                      : -1
              )
              .map(({ shipment, lineItems }) => {
                if (shipment.shippedAt) {
                  return (
                    <ShippedCard
                      key={shipment.id}
                      user={user}
                      lineItems={lineItems}
                      storeOrder={storeOrder}
                      shipment={shipment}
                      setShipmentToEdit={setShipmentToEdit}
                      undoToggler={undoToggler}
                    />
                  )
                }
                // Not shipped yet
                return (
                  <ReadyToShipCard
                    key={shipment.id}
                    user={user}
                    lineItems={lineItems}
                    storeOrder={storeOrder}
                    shipment={shipment}
                    state={storeOrder.state}
                    onMarkAsShipped={onMarkAsShipped}
                  />
                )
              })}
          </>
        ) : null}
      </div>
    </>
  )
}

export default OrderItemCards
